import { useMutation, useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import { MainContent, EmptyInfo, EmptyCard, EmptyIconWrap, EmptyTitle, EmptyDesc, EmptyNote, ScanBtn, ScanBtnWrap } from './styles';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { canScan, scanBySlug } from 'services/asm-service';
import { Dialog, PageHeader } from 'Components/ui';
import { TabAsset } from './TabAsset';
import { TabCert } from './TabCert';
import { TabVulnerability } from './TabVulnerability';
import { PageHelper } from 'Components/PageHelper';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid var(--vo-cloud);
  margin-bottom: 24px;
`;

const TabButton = styled.button`
  padding: 10px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.$active ? 600 : 400};
  color: ${p => p.$active ? 'var(--color-brand)' : 'var(--vo-slate)'};
  border-bottom: ${p => p.$active ? '2px solid var(--color-brand)' : '2px solid transparent'};
  margin-bottom: -2px;
  transition: color 0.2s;
`;

export const AsmDashboard = () => {
  const { t } = useTranslation(['asm']);
  const [tabPosition, setTabPosition] = useState(0);
  const selectedTenant = useSelector(useAuthTenant);

  const { data: cantScan, isLoading: canScanLoading } = useQuery(['getCanScan'], async () => {
    if (selectedTenant.slug === undefined) return true;

    const response = await canScan(selectedTenant.slug);
    if (response.data) {
      return !response.data.canScan;
    }
    return true;
  });

  const mutation = useMutation(
    async () => {
      const response = await scanBySlug(selectedTenant.slug);
      if (response.status === 200) {
        Dialog.fire({
          title: t('asm:dashboard.scanStartedTitle'),
          text: t('asm:dashboard.scanStartedText'),
          icon: 'success',
        });
        setButtonDisabled(true);
      }
    },
    {
      onError: () => {
        Dialog.alert({
          icon: 'info',
          title: t('asm:dashboard.scanRunningTitle'),
          message: t('asm:dashboard.scanRunningText'),
        });
      },
      onSuccess: () => {},
    },
  );

  const [isButtonDisabled, setButtonDisabled] = useState(false);

  return (
    <Container data-tour="asm-dashboard">
      <PageHeader
        title={t('asm:dashboard.pageTitle')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('asm:dashboard.pageTitle') },
        ]}
      />
      {!canScanLoading && !cantScan && (
        <EmptyInfo data-tour="empty-state">
          <EmptyCard>
            <EmptyIconWrap>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </EmptyIconWrap>
            <EmptyTitle>{t('asm:dashboard.emptyTitle')}</EmptyTitle>
            <EmptyDesc>{t('asm:dashboard.emptyDescription')}</EmptyDesc>
            <ScanBtnWrap data-tour="scan-button">
              <ScanBtn
                disabled={cantScan && isButtonDisabled}
                onClick={async () => {
                  const confirmed = await Dialog.confirm({
                    title: t('asm:dashboard.scanConfirmTitle'),
                    message: t('asm:dashboard.scanConfirmInfo'),
                    confirmColor: 'var(--color-brand)',
                    confirmLabel: t('asm:dashboard.scanConfirmYes'),
                    cancelLabel: t('asm:dashboard.scanConfirmCancel'),
                  });
                  if (confirmed) {
                    mutation.mutate();
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {t('asm:dashboard.scanButton')}
              </ScanBtn>
            </ScanBtnWrap>
            <EmptyNote>{t('asm:dashboard.emptyNote')}</EmptyNote>
          </EmptyCard>
        </EmptyInfo>
      )}
      {cantScan && (
        <MainContent>
          <TabBar data-tour="tabs-container">
            <TabButton
              $active={tabPosition === 0}
              id="tab-assets"
              aria-controls="tabpanel-assets"
              data-tour="tab-assets"
              onClick={() => setTabPosition(0)}
            >
              {t('asm:dashboard.tabAssets')}
            </TabButton>
            <TabButton
              $active={tabPosition === 1}
              id="tab-certificates"
              aria-controls="tabpanel-certificates"
              data-tour="tab-certificates"
              onClick={() => setTabPosition(1)}
            >
              {t('asm:dashboard.tabCertificates')}
            </TabButton>
            <TabButton
              $active={tabPosition === 2}
              id="tab-vulnerabilities"
              aria-controls="tabpanel-vulnerabilities"
              data-tour="tab-vulnerabilities"
              onClick={() => setTabPosition(2)}
            >
              {t('asm:dashboard.tabVulnerabilities')}
            </TabButton>
          </TabBar>
          <div role="tabpanel" hidden={tabPosition !== 0} id="tabpanel-assets" aria-labelledby="tab-assets">
            {tabPosition === 0 && (
              <div style={{ padding: 24 }}>
                <TabAsset />
              </div>
            )}
          </div>
          <div role="tabpanel" hidden={tabPosition !== 1} id="tabpanel-certificates" aria-labelledby="tab-certificates">
            {tabPosition === 1 && (
              <div style={{ padding: 24 }}>
                <TabCert />
              </div>
            )}
          </div>
          <div role="tabpanel" hidden={tabPosition !== 2} id="tabpanel-vulnerabilities" aria-labelledby="tab-vulnerabilities">
            {tabPosition === 2 && (
              <div style={{ padding: 24 }}>
                <TabVulnerability />
              </div>
            )}
          </div>
        </MainContent>
      )}
      <PageHelper
        steps={cantScan ? tourStepsWithData(setTabPosition) : tourStepsEmpty}
        tourKey="asm-dashboard"
      />
    </Container>
  );
};

const tourStepsEmpty = [
  {
    target: 'body',
    content: 'Bem-vindo ao ASM (Attack Surface Management)! Esta ferramenta monitora e analisa automaticamente seus domínios em busca de ativos, certificados e vulnerabilidades.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="empty-state"]',
    content: 'Esta é a tela inicial. Nenhum scan foi executado ainda. É necessário iniciar o primeiro scan para começar a monitorar seus domínios.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="scan-button"]',
    content: 'Use este botão para iniciar uma análise completa de todos os domínios cadastrados. O processo leva em média 1 dia para ser concluído.',
    disableScrolling: true,
  },
  {
    target: 'body',
    content: 'Tour concluído! Após iniciar o scan, você poderá visualizar os resultados em 3 abas: Ativos, Certificados e Vulnerabilidades.',
    disableScrolling: true,
    placement: 'center',
  },
];

const tourStepsWithData = (setTabPosition) => [
  {
    target: 'body',
    content: 'Bem-vindo ao ASM (Attack Surface Management)! Aqui você visualiza os resultados das análises dos seus domínios.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="tabs-container"]',
    content: 'Os resultados são organizados em 3 abas: Ativos (recursos descobertos), Certificados (SSL/TLS) e Vulnerabilidades (riscos identificados).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="tab-assets"]',
    content: 'Vamos explorar a aba Ativos, onde você visualiza todos os recursos descobertos durante o scan.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(0);
      await new Promise(resolve => setTimeout(resolve, 500));
    },
  },
  {
    target: '[data-tour="charts-container"]',
    content: 'Estes gráficos mostram a distribuição de CDN (Content Delivery Network) e WAF (Web Application Firewall) encontrados nos seus ativos.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cdn-chart"]',
    content: 'O gráfico CDN mostra quais provedores de rede de entrega de conteúdo estão sendo utilizados (Cloudflare, Akamai, etc.).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="waf-chart"]',
    content: 'O gráfico WAF exibe os firewalls de aplicação web detectados, que protegem seus ativos contra ataques.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="assets-table"]',
    content: 'Esta tabela lista todos os ativos descobertos, incluindo subdomínios, endereços IP, tecnologias utilizadas e status de cada recurso.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="tab-certificates"]',
    content: 'Agora vamos para a aba Certificados, onde você monitora os certificados SSL/TLS dos seus domínios.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(1);
      await new Promise(resolve => setTimeout(resolve, 500));
    },
  },
  {
    target: '[data-tour="cert-overview"]',
    content: 'Esta seção apresenta uma visão geral dos certificados: contador total e distribuição por emissor.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-counter"]',
    content: 'Este card mostra o número total de certificados SSL/TLS encontrados nos seus domínios.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-issuers"]',
    content: 'O gráfico de Emissores mostra quais autoridades certificadoras (Let\'s Encrypt, DigiCert, etc.) emitiram os certificados dos seus domínios.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-table"]',
    content: 'Esta tabela lista todos os certificados com informações detalhadas: domínio, emissor, datas de validade, status de expiração e mais.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="tab-vulnerabilities"]',
    content: 'Por fim, a aba Vulnerabilidades, onde você monitora todas as falhas de segurança identificadas.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(2);
      await new Promise(resolve => setTimeout(resolve, 300));
    },
  },
  {
    target: '[data-tour="vuln-overview"]',
    content: 'Esta seção apresenta uma visão geral: contador total de vulnerabilidades e distribuição por severidade.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-counter"]',
    content: 'Este card mostra o número total de vulnerabilidades encontradas nos seus ativos.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="severity-chart"]',
    content: 'O gráfico de Severidades mostra a distribuição das vulnerabilidades por nível de criticidade: Crítica, Alta, Média, Baixa.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-chart"]',
    content: 'Este gráfico detalha os tipos de vulnerabilidades encontradas, mostrando a quantidade de cada tipo identificado.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-table"]',
    content: 'Esta tabela lista todas as vulnerabilidades com informações detalhadas: título, descrição, severidade, domínio afetado e recomendações de correção.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! O ASM monitora continuamente sua superfície de ataque. Priorize a correção das vulnerabilidades críticas e monitore os certificados regularmente.',
    disableScrolling: true,
    placement: 'center',
  },
];
